<!--
  Right-hand builder canvas: tab strip plus scrollable grid for the active tab.
-->
<script setup lang="ts">
import type { BuilderTab, DragPayload, ObjectInventoryField } from '../model/types'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { GridRowGroup } from '../model/useGridOperations'
import PassportBuilderGrid from './PassportBuilderGrid.vue'
import PassportBuilderTabBar from './PassportBuilderTabBar.vue'

withDefaults(
  defineProps<{
    tabs: BuilderTab[]
    activeTabId: string
    rows: GridRowGroup[]
    dropTargetRow?: number | null
    allFields?: ObjectInventoryField[]
    dragPayload?: DragPayload | null
    catalogTypeId?: number
  }>(),
  {
    dropTargetRow: null,
    allFields: () => [],
    dragPayload: null,
    catalogTypeId: 0,
  },
)

type GridFieldRemovePayload = { id: string; row: number; cell: number }
type GridFieldResizePayload = GridFieldRemovePayload & { newSpan: number }
type HeadingRenamePayload = GridFieldRemovePayload & { name: string }
type FieldSettingsPayload = GridFieldRemovePayload & { mode: 'catalog' | 'reference' }
type ReorderFieldPayload = GridFieldRemovePayload & { targetIndex: number }

const emit = defineEmits<{
  'update:activeTabId': [id: string]
  'add-tab': []
  'remove-tab': [id: string]
  'rename-tab': [id: string, newLabel: string]
  'reorder-tabs': [draggedTabId: string, targetTabId: string]
  drop: [rowNum: number, event: DragEvent]
  'remove-field': [payload: GridFieldRemovePayload]
  'resize-field': [payload: GridFieldResizePayload]
  'rename-heading': [payload: HeadingRenamePayload]
  'rename-field': [payload: HeadingRenamePayload]
  'field-settings': [payload: FieldSettingsPayload]
  'remove-row': [rowNum: number]
  'add-row': []
  'drag-field-start': [payload: DragPayload, event: DragEvent]
  'drag-over-row': [rowNum: number]
  reorder: [rowNum: number, payload: ReorderFieldPayload]
  'patch-table-field': [fieldId: string, updater: (f: GridFieldType) => GridFieldType]
  'builder-drag-end': []
  'summary-field-settings': [payload: { tableFieldId: string; layoutItemId: string }]
}>()
</script>

<template>
  <div class="pb-canvas-area">
    <PassportBuilderTabBar
      :tabs="tabs"
      :active-tab-id="activeTabId"
      @update:active-tab-id="emit('update:activeTabId', $event)"
      @add-tab="emit('add-tab')"
      @remove-tab="emit('remove-tab', $event)"
      @rename-tab="(id, label) => emit('rename-tab', id, label)"
      @reorder-tabs="(a, b) => emit('reorder-tabs', a, b)"
    />
    <div class="pb-canvas-scroll">
      <PassportBuilderGrid
        :rows="rows"
        :drop-target-row="dropTargetRow"
        :all-fields="allFields"
        :drag-payload="dragPayload"
        :catalog-type-id="catalogTypeId"
        @drop="(rowNum, ev) => emit('drop', rowNum, ev)"
        @remove-field="emit('remove-field', $event)"
        @resize-field="emit('resize-field', $event)"
        @rename-heading="emit('rename-heading', $event)"
        @rename-field="emit('rename-field', $event)"
        @field-settings="emit('field-settings', $event)"
        @remove-row="emit('remove-row', $event)"
        @add-row="emit('add-row')"
        @drag-field-start="(p, e) => emit('drag-field-start', p, e)"
        @drag-over-row="emit('drag-over-row', $event)"
        @reorder="(row, payload) => emit('reorder', row, payload)"
        @patch-table-field="(id, u) => emit('patch-table-field', id, u)"
        @builder-drag-end="emit('builder-drag-end')"
        @summary-field-settings="(p) => emit('summary-field-settings', p)"
      />
    </div>
  </div>
</template>
